/**
 * StatusTabs — extracted from PR-1's smoke list. Tabs across the six
 * statuses that matter on the invoices list (per the prototype):
 *
 *   All · Draft · Sent · Overdue · Paid · Voided
 *
 * `partially_paid` rolls under the row's status pill in the table
 * but doesn't get its own tab in V1.0.x — same as the prototype.
 *
 * Pure presentation over the shared `@flint/ui` Tabs primitive (the
 * underlined-tab pattern, brand-spec §13.2). Counts come from the
 * parent's `invoices.list` query.
 */
import { TabsContent, TabsList, TabsRoot, TabsTrigger } from "@flint/ui";

export type StatusFilter =
  | "all"
  | "draft"
  | "issued"
  | "partially_paid"
  | "paid"
  | "overdue"
  | "void"
  | "reversed";

export interface StatusTabsProps {
  value: StatusFilter;
  onChange: (next: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}

const TABS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "draft", label: "Draft" },
  { value: "issued", label: "Sent" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "void", label: "Voided" },
  { value: "reversed", label: "Reversed" },
];

export function StatusTabs({ value, onChange, counts }: StatusTabsProps) {
  return (
    <TabsRoot value={value} onValueChange={(v) => onChange(v as StatusFilter)}>
      <TabsList aria-label="Invoice status">
        {TABS.map((t) => (
          <TabsTrigger
            key={t.value}
            value={t.value}
            count={counts[t.value]}
            countTone={t.value === "overdue" ? "warn" : "default"}
          >
            {t.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {/* Each Radix TabsTrigger sets aria-controls to a panel id; the filtered
          list below isn't a Radix panel, so without these the references dangle
          (axe aria-valid-attr-value, critical). forceMount keeps an empty,
          screen-reader-hidden panel per tab so every reference resolves. */}
      {TABS.map((t) => (
        <TabsContent key={t.value} value={t.value} forceMount className="hidden" aria-hidden />
      ))}
    </TabsRoot>
  );
}
